/**
 * 사람 아바타 — 이니셜 원형. 목업 `.mem-av`(멤버관리) / `.mgr .av`(현장관리) 공통.
 *
 * **색은 값 구분 팔레트다.** 목업이 6색(violet/blue/emerald/amber/rose/slate)을 사람마다 다르게
 * 주는 이유는 장식이 아니라 "같은 성씨 이니셜이 줄지어 있어도 행이 구분된다"는 정보 기능이다.
 * 그래서 brand 한 색으로 통일하면(리스킨 1차의 상태) 그 기능이 사라진다 — 팔레트는 팔레트로 두고,
 * 명도 램프만 목업 값(400 → 600)에 맞춘다.
 *
 * 색은 **seed 해시로 결정론적으로** 고른다. 목록이 다시 정렬돼도 같은 사람은 같은 색이어야
 * 하므로 배열 index가 아니라 안정적인 값(멤버 id)을 seed로 넘길 것.
 *
 * Tailwind는 클래스를 정적으로 스캔하므로 완성형 리터럴 맵으로 둔다(동적 조합 금지).
 *
 * ⚠️ **팔레트를 복제하지 말 것.** 멤버 목록과 현장 목록이 같은 사람을 서로 다른 색으로 그리면
 * "색으로 사람을 구분한다"는 기능 자체가 깨진다. 처음엔 `pages/members/` 안에 있었고
 * `pages/sites/`가 cross-page import 하던 것을 이 자리로 올렸다(2026-07-28).
 * admin도 아바타가 필요해지면 그때 `@safetyhub/ui` 승격을 검토한다 — 지금은 web 전용이다.
 */
const AVATAR_GRADIENT = [
  'from-violet-400 to-violet-600',
  'from-blue-400 to-blue-600',
  'from-emerald-400 to-emerald-600',
  'from-amber-400 to-amber-600',
  'from-rose-400 to-rose-600',
  'from-ink-400 to-ink-500',
] as const;

/** 지름·글자 크기 — 목업 `.mgr .av` 28px / 모바일 카드 36px / `.mem-av` 42px / `.mm-avatar` 64px. */
const SIZE_CLASS = {
  sm: 'h-7 w-7 text-[11.5px]',
  md: 'h-9 w-9 text-sm',
  lg: 'h-[42px] w-[42px] text-[15px]',
  xl: 'h-16 w-16 text-2xl',
} as const;

export type PersonAvatarSize = keyof typeof SIZE_CLASS;

/**
 * seed → 팔레트 index. 문자열 해시(djb2 축약)라 같은 입력이면 항상 같은 색이 나온다.
 * seed가 비면 마지막 slate로 떨어뜨린다 — 임의 색을 주면 "정보가 있는 것처럼" 보인다.
 */
export function getAvatarGradientClass(seed: string | null | undefined): string {
  if (!seed) return AVATAR_GRADIENT[AVATAR_GRADIENT.length - 1];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
  }
  return AVATAR_GRADIENT[hash % AVATAR_GRADIENT.length];
}

export interface PersonAvatarProps {
  /** 표시 이름. 첫 글자를 이니셜로 쓴다. */
  name?: string | null;
  /**
   * 색 결정용 안정 키(멤버 id 등). 생략하면 name으로 떨어진다 —
   * 동명이인이 같은 색이 되지만 이름조차 없는 것보다는 구분된다.
   */
  seed?: string | null;
  /** 크기. 기본 lg(42px, 데스크톱 표). */
  size?: PersonAvatarSize;
  /** 추가 클래스(위치·flex 제어용). 색·크기는 prop이 소유한다. */
  className?: string;
}

export function PersonAvatar({ name, seed, size = 'lg', className = '' }: PersonAvatarProps) {
  const initial = name?.trim().charAt(0) || 'U';
  return (
    <div
      aria-hidden="true"
      className={`flex flex-none items-center justify-center rounded-full bg-gradient-to-br font-bold tracking-[-0.01em] text-white ${SIZE_CLASS[size]} ${getAvatarGradientClass(seed ?? name)} ${className}`}
    >
      {initial}
    </div>
  );
}

export default PersonAvatar;
